import {noop} from '@benchling/js-utils';
import Backbone from 'backbone';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import asm from 'assembly/Asm';
import * as util from 'biojs/Util';
import {Sequence} from 'components/Sequence';
import {MonomerNaturalAnalogue} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  getModsForUTFlexibleNaturalBasesIfEnabled,
  isFullUTFlexibilityEnabled,
  lockImplicitMods,
} from 'modbio/util';
import {InputBubble, InputBubbleProps, INPUT_BUBBLE_OPERATION} from 'molbio/maps/InputBubble';
import {getNaturalMonomerId} from 'molbio/monomers/MonomersUtil';
import * as sequtil from 'molbio/SeqUtil';
import notifications from 'notifications';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import * as bclip from 'util/BenchlingClipboard';

interface InputBubbleOptions {
  isPositionedAbove: boolean;
  left: number;
  model: Sequence;
  offset: number;
  pos: number;
  refocusTo: HTMLElement | null;
  operation: INPUT_BUBBLE_OPERATION;
  top: number;
  pasteText?: string;
  doPaste?: () => void;
}

export type InputBubblePositionDetails = Pick<
  InputBubbleOptions,
  'top' | 'left' | 'offset' | 'isPositionedAbove'
>;

interface InputBubbleView extends InputBubbleOptions {
  killId: number | null;
  doPaste: () => void;
}

// This is a hint to TS to use the browser rather than the Node signature for setTimeout
// eslint-disable-next-line @typescript-eslint/unbound-method
const {setTimeout} = window;

class InputBubbleView extends Backbone.View<Sequence> {
  initialize(options: InputBubbleOptions): void {
    this.killId = null;
    this.model = options.model;
    this.operation = options.operation;
    this.refocusTo = options.refocusTo;
    this.pos = options.pos;
    this.top = options.top;
    this.left = options.left;
    this.offset = options.offset;
    this.isPositionedAbove = options.isPositionedAbove;
    this.pasteText = options.pasteText;
    this.doPaste = options.doPaste ?? noop;
  }

  remove(): InputBubbleView {
    this.trigger('remove');
    super.remove();
    return this;
  }

  schedRemove(): void {
    if (!this.killId) {
      this.killId = setTimeout(() => {
        this.killId = null;
        this.remove();
      }, 1);
    }
  }

  unschedRemove(): void {
    if (this.killId != null) {
      clearTimeout(this.killId);
      this.killId = null;
    }
  }

  getModifiedNucleotideCount(bases: string, clipboardMeta: bclip.SerializedClipboardMeta): number {
    let mods = clipboardMeta['copied-modified-nucleotides'] ?? [];
    if (!this.useUTFlex() || mods.length === 0) {
      return mods.length;
    }

    mods = lockImplicitMods(
      bases,
      mods,
      clipboardMeta['copied-sourceNucleotideType']!,
      this.model.nucleotideType()
    );

    const flexibleBaseId =
      this.model.nucleotideType() === util.NucleotideType.DNA
        ? getNaturalMonomerId(MonomerNaturalAnalogue.U)
        : getNaturalMonomerId(MonomerNaturalAnalogue.T);
    const naturalSugarId =
      this.model.nucleotideType() === util.NucleotideType.DNA
        ? getNaturalMonomerId(MonomerNaturalAnalogue.D_LOWER)
        : getNaturalMonomerId(MonomerNaturalAnalogue.R_LOWER);
    const naturalPhosphateId = getNaturalMonomerId(MonomerNaturalAnalogue.P_LOWER);
    return mods.filter(
      (mod) =>
        mod.baseMonomerId !== flexibleBaseId ||
        mod.sugarMonomerId !== naturalSugarId ||
        mod.phosphateMonomerId !== naturalPhosphateId
    ).length;
  }

  getRenderProps(): InputBubbleProps {
    const sanitizeBases = (residues: string): string =>
      util.sanitize(residues, this.model.nucleotideType(), this.useUTFlex());
    let featureCounts = null;
    let pastedBases = '';
    if (this.pasteText) {
      const data = bclip.clipboard.grab(this.pasteText);
      // TODO: should consolidate some of this logic with SequenceMapUtil.
      if (data.state === bclip.ClipboardGrabState.RAW) {
        pastedBases = sanitizeBases(data.text);
      } else {
        pastedBases = data.text;
      }
      if (!this.model.isOligo()) {
        featureCounts = {
          annotation: (data.meta['copied-annotations'] ?? []).length,
          ...(this.model.supportsChemicalMods() && {
            'modified nucleotide': (data.meta['copied-modified-nucleotides'] ?? []).length,
          }),
          translation: (data.meta['copied-translations'] ?? []).length,
          primer: (data.meta['copied-sequencePrimers'] ?? []).length,
          part: (data.meta['copied-parts'] ?? []).length,
        };
      } else {
        featureCounts = {
          annotation: (data.meta['copied-annotations'] ?? []).length,
          ...(this.model.supportsChemicalMods() && {
            'modified nucleotide': this.getModifiedNucleotideCount(pastedBases, data.meta),
          }),
        };
      }
    }

    return {
      pastedBases,
      featureCounts,
      operation: this.operation,
      selection: this.model.get('selection'),
      top: this.top,
      left: this.left,
      offset: this.offset,
      isPositionedAbove: this.isPositionedAbove,
      sanitizeResidues: sanitizeBases,
      onRequestClose: () => {
        this.removeFunc();
      },
      onInsertResidues: (bases, position) => {
        this.doInsertBases(bases, position);
      },
      onReplaceOrDeleteResidues: (bases, start, end) => {
        this.doReplaceBases(bases, start, end);
      },
      residueName: 'base',
      sequenceStartIndex: this.model.get('_sequence').get('startIndex'),
      onCheckNextResidueValid: (residues: string) =>
        (this.useUTFlex() && util.isValidMixedBaseSequence(residues)) ||
        util.isValidBase(residues, this.model.get('_sequence').baseTable()),
      onPaste: this.doPaste,
    };
  }

  removeFunc: () => void = () => {
    const focusTarget = this.refocusTo || this.$el.parents('.buffer-tab');
    focusTarget.focus();
    this.remove();
  };

  useUTFlex(): boolean {
    return this.model.get('_sequence').isOligo() && isFullUTFlexibilityEnabled();
  }

  doReplaceBases(bases: string, start: number, end: number): void {
    let deferred;
    let resetSelection;
    const _sequence = this.model.get('_sequence');
    const selection = this.model.get('selection');
    let options = {};
    if (bases.length > 0 && this.useUTFlex()) {
      options = {
        modifiedNucleotides: getModsForUTFlexibleNaturalBasesIfEnabled(
          bases,
          _sequence.nucleotideType(),
          _sequence.get('type')
        ),
      };
      // Once the mods have been calculated, re-sanitize to coerce any mixed bases to the destination type
      bases = util.sanitize(bases, _sequence.nucleotideType());
    }
    // Compute start/end before actually performing edit since with optimistic rendering, they will
    // immediately update the selection.
    const [startAfterInsert, endAfterInsert] = sequtil.postEditSelection(
      selection.start(),
      selection.end(),
      bases.length
    );

    const seqLength = _sequence.length();
    const selectionLength = sequtil.length(start, end, seqLength);

    if (bases.length === 0) {
      // When replacing with no bases, treat as a delete.
      if (!asm.of(_sequence).canDelete(_sequence, selection, notifications.error)) {
        return;
      }

      resetSelection = () => {
        selection.reset(Math.min(start, seqLength - selectionLength));
      };
      deferred = this.model.deleteBases(start, end);
    } else {
      if (!asm.of(_sequence).canReplace(_sequence, selection, bases, notifications.error)) {
        return;
      }
      resetSelection = () => {
        selection.setEndpoints(startAfterInsert, endAfterInsert);
      };
      deferred = this.model.replace(start, end, bases, options);
      analytics.track('editor', 'replaced_bases', {source: 'input-bubble', bases: bases.slice(0, 100)});
    }

    // Handle xhr success/failure
    deferred.fail(notifications.notifyError);
    deferred.client
      .done(() => {
        resetSelection();
      })
      .always(this.removeFunc);

    this._maybeUpdateAssemblyAfterInput(deferred, resetSelection, bases.length === 0, selectionLength, bases);
  }

  doInsertBases(bases: string, position: number): void {
    const _sequence = this.model.get('_sequence');
    const selection = this.model.get('selection');
    const selectionLength = 0;
    if (bases.length === 0) {
      // Inserting no bases is a noop.
      this.removeFunc();
      return;
    }
    // Compute start/end before actually performing edit since with optimistic rendering, they will
    // immediately update the selection.
    const [startAfterInsert, endAfterInsert] = sequtil.postEditSelection(
      selection.start(),
      selection.end(),
      bases.length
    );
    const resetSelection = (): void => {
      selection.setEndpoints(startAfterInsert, endAfterInsert);
    };
    let options = {};
    if (this.useUTFlex()) {
      options = {
        modifiedNucleotides: getModsForUTFlexibleNaturalBasesIfEnabled(
          bases,
          _sequence.nucleotideType(),
          _sequence.get('type')
        ),
      };
      // Once the mods have been calculated, re-sanitize to coerce any mixed bases to the destination type
      bases = util.sanitize(bases, _sequence.nucleotideType());
    }
    const deferred = this.model.insert(position, bases, options);
    analytics.track('editor', 'inserted_bases', {source: 'input-bubble', bases: bases.slice(0, 100)});

    // Handle xhr success/failure
    deferred.fail(notifications.notifyError);
    deferred.client
      .done(() => {
        resetSelection();
      })
      .always(this.removeFunc);
    this._maybeUpdateAssemblyAfterInput(deferred, resetSelection, bases.length === 0, selectionLength, bases);
  }

  _maybeUpdateAssemblyAfterInput(
    deferred: JQueryPromise<void>,
    resetSelection: () => void,
    isDeleteOperation: boolean,
    selectionLength: number,
    bases: string
  ): void {
    const _sequence = this.model.get('_sequence');
    const selection = this.model.get('selection');
    // TODO: Show a loading screen while assembly update is taking place to be "atomic"
    if (asm.of(_sequence).assemblyType != null) {
      deferred.done(() => {
        resetSelection(); // Reset the selection in case the user moved it.
        // Note it's important to do this after updating the selection so that the handler gets
        // the new selection.
        if (this.operation !== INPUT_BUBBLE_OPERATION.INSERT) {
          if (isDeleteOperation) {
            asm
              .of(_sequence)
              .didDelete(_sequence, selection, selectionLength, notifications.success, notifications.error);
          } else {
            asm
              .of(_sequence)
              .didReplace(_sequence, selection, bases, notifications.success, notifications.error);
          }
        } else {
          asm
            .of(_sequence)
            .didInsert(_sequence, selection.start(), bases, notifications.success, notifications.error);
        }
      });
    }
  }

  updatePosition(pos: InputBubblePositionDetails): void {
    this.top = pos.top;
    this.left = pos.left;
    this.offset = pos.offset;
    this.isPositionedAbove = pos.isPositionedAbove;
  }

  render(): InputBubbleView {
    renderToDOM(<InputBubble {...this.getRenderProps()} />, this.el);
    return this;
  }
}

export default InputBubbleView;
